import React, { useState, useRef, useEffect } from 'react';
import './TimeOffCalendar.css';
import { 
  Button, 
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
  Card,
  CardHeader,
  CardBody
} from 'reactstrap';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import Dropzone from "react-dropzone";
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Calendar, ChevronLeft, ChevronRight, Plus, Search, Paperclip } from 'lucide-react';
import { getTimeOffList,addTimeOff,addDocumentFile,updateTimeOff,deleteTimeOff} from '../../../utils/timeoff';
 

const TimeOffDashboard = () => {
  // States
  const [viewMode, setViewMode] = useState('year'); // Default to year view
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentYear, setCurrentYear] = useState(2025);
  const [modalOpen, setModalOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('Paid');
  const [dateRange, setDateRange] = useState({
    start: '05/15/2025',
    end: '05/15/2025'
  });
  const [title, setTitle] = useState(null);
  const [allday, setAllday] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [requestDescription, setRequestDescription] = useState('');
  const [pendingRequests, setPendingRequests] = useState(0);
  const [events, setEvents] = useState([ ]);
  const [edit, setEdit] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  // For drag selection in year view
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [eventDetail, setEventDetail] = useState({});
  const calendarRef = useRef(null);

  useEffect(() => {
    // Set the year from the currentDate
    loadEvents();
  }, [currentDate]);

  useEffect(() => {
    // Set the year from the currentDate
    setCurrentYear(currentDate.getFullYear());
  }, [currentDate]);
  
  // Toggle functions with safety checks
  const toggleModal = () => {
    try {
      setModalOpen(prev => !prev);
    } catch (error) {
      console.error('Error toggling modal:', error);
      setModalOpen(false);
    }
  };
  
  const toggleTypeDropdown = () => {
    try {
      setTypeDropdownOpen(prev => !prev);
    } catch (error) {
      console.error('Error toggling type dropdown:', error);
      setTypeDropdownOpen(false);
    }
  };
  
  const toggleViewDropdown = () => {
    try {
      setViewDropdownOpen(prev => !prev);
    } catch (error) {
      console.error('Error toggling view dropdown:', error);
      setViewDropdownOpen(false);
    }
  };
  
  const toggleSearchDropdown = () => {
    try {
      setSearchDropdownOpen(prev => !prev);
    } catch (error) {
      console.error('Error toggling search dropdown:', error);
      setSearchDropdownOpen(false);
    }
  };
  
  // Switch between year and month views
  const changeViewMode = (mode) => {
    setViewMode(mode);
    setViewDropdownOpen(false);
  };
  // Load events from API (dummy data)
  const loadEvents = async () => {
    try {
        const param={
            start_date:currentDate.getFullYear()+'-01-01',
            end_date:currentDate.getFullYear()+'-12-31'
        }
      const response = await getTimeOffList(param);
      if (response.status === 200) {
         setEvents(response.data.data);
      } 
    }
    catch (error) {
      console.error('Error fetching events:', error); 
    } 
  }
  // Handle date navigation
  const navigatePrevious = () => {
    if (viewMode === 'year') {
      setCurrentYear(currentYear - 1);
    } else {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.prev();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const navigateNext = () => {
    if (viewMode === 'year') {
      setCurrentYear(currentYear + 1);
    } else {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.next();
      setCurrentDate(calendarApi.getDate());
    }
  };
  const handleEventClick = (info) => {
    setEventDetail({
      id: info.event.id,
      title: info.event.title,
      start: info.event.startStr,
      end: info.event.endStr,
      description: info.event.extendedProps.description || '',
      all_day: info.event.allDay,
    });
    setEdit(false);
    setInfoModal(true);
  }
  // Handle date selection in FullCalendar (month view)
  const handleDateSelect = (selectInfo) => {
    const start = new Date(selectInfo.startStr);
    const end = new Date(selectInfo.endStr);
    
    // Format dates for display
    const startFormatted = formatDate(start);
    const endFormatted = formatDate(new Date(end.setDate(end.getDate() - 1))); // Adjust end date
    
    setDateRange({
      start: startFormatted,
      end: endFormatted
    });
    
    toggleModal();
  };

  // Format date as MM/DD/YYYY
  const formatDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  
  // Handle type selection
  const selectType = (type) => {
    setSelectedType(type);
  };
  
const handleFileListUpload = async (files) => {
  if (!files.length) return;

  const formData = new FormData();
  
  // Append multiple files
  files.forEach(file => {
      formData.append("file", file);
  });

  const response = await addDocumentFile(formData);

  if (response && response.success) {
      // Map response URLs back to the original file object schema
      const updatedFiles = files.map((file, index) => ({
          ...file,
          id: file.id,
          preview: URL.createObjectURL(file),
          formattedSize: formatBytes(file.size),
          url: response.images[index]?`${baseURL}/file/product/image/${response.images[index]}`: file.url, // Use returned URL if available
          filename: response.images[index]
      }));

      setSelectedFiles(updatedFiles);
  }
};
  // Handle request submission
  const handleSubmitRequest = async () => {
    const newEvent = {
        id: Date.now(),
      title: title,
      title: selectedType,
      start: convertToISODate(dateRange.start),
      end: convertToISODate(dateRange.end, true), 
      description: requestDescription,
      all_day: allday,
      supporting_document_filename:selectedFiles.map((file) => {
        return file.url.substring(file.url.lastIndexOf('/') + 1);
      })
    };
    
    try {
    // Add event to state
    const response = await addTimeOff(newEvent);
    if (response.status === 200||response.status === 201) {
        loadEvents();
        toggleModal();
    }
    } catch (error) {
        console.error('Error adding event:', error); 
    }
    // setEvents([...events, newEvent]);
  };
  
  // Convert MM/DD/YYYY to YYYY-MM-DD for FullCalendar
  const convertToISODate = (dateStr, isEndDate = false) => {
    const [month, day, year] = dateStr.split('/');
    if (isEndDate) {
      // FullCalendar's end date is exclusive, so we add a day
      const date = new Date(`${year}-${month}-${day}`);
      date.setDate(date.getDate() + 1);
      return date.toISOString().split('T')[0];
    }
    return `${year}-${month}-${day}`;
  };
  const getEventClass = (type) => { 
    switch(type) {
      case 'Pending':
        return 'calendar-event-pending';
      case 'Approve':
        return 'calendar-event-approve';
      case 'Refused':
        return 'calendar-event-refused';
      default:
        return '';
    }
  };
  // Get color based on event type
  const getTypeClass = (type) => { 
    switch(type) {
      case 'Paid':
        return 'calendar-event-paid';
      case 'Unpaid':
        return 'calendar-event-unpaid';
      case 'Sick':
        return 'calendar-event-sick';
      case 'Vacation':
        return 'calendar-event-vacation';
      case 'Extra Hours':
        return 'calendar-event-extrahours';
      case 'MISC':
        return 'calendar-event-MISC';
      default:
        return '';
    }
  }; 
  // Handle today button click
  const goToToday = () => {
    if (viewMode === 'year') {
      setCurrentYear(new Date().getFullYear());
    } else {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.today();
      setCurrentDate(calendarApi.getDate());
    }
  };
  
  // Check if an event exists on a specific date
  const hasEventOnDate = (year, month, day) => {
    const dateToCheck = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.some(event => {
      const eventStart = event.start.substring(0, 10);
      const eventEnd = event.end ? event.end.substring(0, 10) : eventStart;
      return dateToCheck >= eventStart && dateToCheck <= eventEnd;
    });
  };
  const getEventOnDate = (year, month, day) => {
    const dateToCheck = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.find(event => {
      const eventStart = event.start.substring(0, 10);
      const eventEnd = event.end ? event.end.substring(0, 10) : eventStart;
      return dateToCheck >= eventStart && dateToCheck <= eventEnd;
    });
  };
  // Calculate the days in a month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  // Calculate the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };
  
  // Handle click on a date with event to show info modal
  const handleDateClick = (year, month, day) => {
    const event = getEventOnDate(year, month, day);
    if (event) {
      setEventDetail({
        id: event.id,
        title: event.title,
        start: event.start,
        end: event.end,
        description: event.description || '',
        allDay: true,
        opinion: event.opinion || ''
      });
      setEdit(false);
      setInfoModal(true);
    }
  };
  
  // Convert date coordinates to actual date
  const getDateFromCoords = (year, month, day) => {
    return { year, month, day };
  };
  
  // Sort dates to get the correct start and end
  const sortDates = (date1, date2) => {
    const d1 = new Date(date1.year, date1.month, date1.day);
    const d2 = new Date(date2.year, date2.month, date2.day);
    
    if (d1 <= d2) {
      return { start: date1, end: date2 };
    } else {
      return { start: date2, end: date1 };
    }
  };
  
  // Handle mouse down on a date cell (start dragging)
  const handleDateMouseDown = (year, month, day, hasEvent) => {
    if (day <= 0 || day > getDaysInMonth(year, month)) return;
    
    // If there's an event on this date, show the info modal instead of starting drag
    if (hasEvent) {
      handleDateClick(year, month, day);
      return;
    }
    
    const dateCoords = getDateFromCoords(year, month, day);
    setIsDragging(true);
    setDragStart(dateCoords);
    setDragEnd(dateCoords);
    setSelectedDates([dateCoords]);
  };
  
  // Handle mouse enter during drag
  const handleDateMouseEnter = (year, month, day) => {
    if (!isDragging || day <= 0 || day > getDaysInMonth(year, month)) return;
    
    const dateCoords = getDateFromCoords(year, month, day);
    setDragEnd(dateCoords);
    
    // Calculate all dates in the range
    const { start, end } = sortDates(dragStart, dateCoords);
    const rangeStartDate = new Date(start.year, start.month, start.day);
    const rangeEndDate = new Date(end.year, end.month, end.day);
    
    const dates = [];
    const currentDate = new Date(rangeStartDate);
    
    while (currentDate <= rangeEndDate) {
      dates.push({
        year: currentDate.getFullYear(),
        month: currentDate.getMonth(),
        day: currentDate.getDate()
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    setSelectedDates(dates);
  };
  
  // Handle mouse up (end dragging and open modal)
  const handleDateMouseUp = (year, month, day) => {
    if (!isDragging || day <= 0 || day > getDaysInMonth(year, month)) return;
    
    setIsDragging(false);
    
    if (dragStart && dragEnd) {
      const { start, end } = sortDates(dragStart, dragEnd);
      
      const startDate = new Date(start.year, start.month, start.day);
      const endDate = new Date(end.year, end.month, end.day);
      
      // Format dates for the modal
      setDateRange({
        start: formatDate(startDate),
        end: formatDate(endDate)
      });
      
      toggleModal();
    }
    
    // Reset drag state
    setDragStart(null);
    setDragEnd(null);
    setSelectedDates([]);
  };
  
  // Handle mouse leave from calendar area
  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      setSelectedDates([]);
    }
  };
  
  // Check if a date is in the selected range
  const isDateSelected = (year, month, day) => {
    return selectedDates.some(date => 
      date.year === year && date.month === month && date.day === day
    );
  };
  
  // Render the top navigation bar
  const renderTopNav = () => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="d-flex align-items-center">
        <div className="mr-2">
          <Button color="light" className="rounded-circle p-1" onClick={navigatePrevious}>
            <ChevronLeft size={16} />
          </Button>
          <Button color="light" className="rounded-circle p-1 ml-1" onClick={navigateNext}>
            <ChevronRight size={16} />
          </Button>
        </div>
        <Dropdown isOpen={viewDropdownOpen} toggle={toggleViewDropdown}>
          <DropdownToggle caret color="light">
            {viewMode === 'year' ? 'Year' : 'Month'}
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem onClick={() => changeViewMode('year')}>Year</DropdownItem>
            <DropdownItem onClick={() => changeViewMode('month')}>Month</DropdownItem>
          </DropdownMenu>
        </Dropdown>
        <Button color="light" className="ml-2" onClick={goToToday}>Today</Button>
        <div className="ml-3 font-weight-bold">
          {viewMode === 'year' ? currentYear : `${currentDate.toLocaleString('default', { month: 'long' })} ${currentYear}`}
        </div>
      </div> 
    </div>
  );
  
  // Render the pending requests card
  const renderPendingRequests = () => (
    <Card className="mb-3 text-center">
      <CardBody>
        <div>Pending Requests</div>
        <div style={{ fontSize: '2rem' }}>{pendingRequests}</div>
        <Button color="link" className="p-0">NEW ALLOCATION REQUEST</Button>
      </CardBody>
    </Card>
  );
  
  // Render the legend
  const renderLegend = () => ( 
    <Card>
      <CardHeader>Legend</CardHeader>
      <CardBody>
        <div className='mb-5' style={{fontSize:'20px'}}>
          <div className="mb-2">
            <div  className=" calendar-event-paid rounded-pill"  style={{width: '50px', height: '20px', marginRight:'1em', display: 'inline-block'}}></div>
            <span>Paid</span> 
          </div>
          <div  className="mb-2">
            <div  className="mr-2 calendar-event-unpaid rounded-pill"   style={{width: '50px', height: '20px',marginRight:'1em', display: 'inline-block'}}></div>
            <span>Unpaid</span> 
          </div>
          <div className="mb-2">
            <div  className=" calendar-event-sick rounded-pill"  style={{width: '50px', height: '20px', marginRight:'1em', display: 'inline-block'}}></div>
            <span>Sick</span> 
          </div>
          <div className="mb-2">
            <div  className=" calendar-event-vacation rounded-pill"  style={{width: '50px', height: '20px', marginRight:'1em', display: 'inline-block'}}></div>
            <span>Vacation</span> 
          </div>
          <div className="mb-2">
            <div   className="mr-2  calendar-event-extrahours rounded-pill"   style={{width: '50px', height: '20px',marginRight:'1em',  display: 'inline-block'}}></div>
             <span>extra hours</span> 
          </div>
        </div>
        
        <div  style={{fontSize:'20px'}}>
          <div className="mb-2">
            <div  className="mr-2 calendar-legend rounded-pill"   style={{width: '50px', height: '20px',marginRight:'1em',  display: 'inline-block'}}></div>
            Pending
          </div>
          <div className="mb-2">
            <div  className="mr-2 calendar-legend calendar-event-approve rounded-pill"   style={{width: '50px', height: '20px', marginRight:'1em', display: 'inline-block'}}></div>
            Approve
          </div>
          <div>
            <div  className="mr-2 calendar-legend calendar-event-refused rounded-pill"   style={{width: '50px', height: '20px', marginRight:'1em', display: 'inline-block'}}></div>
            Refused
          </div>
        </div>
      </CardBody>
    </Card>
  );
  const InfoModal = () => {
    return (
      <Modal isOpen={infoModal} toggle={() => setInfoModal(false)} centered>
        <ModalHeader toggle={() => setInfoModal(false)}>Event Details</ModalHeader>
        <ModalBody>
          <p><strong>Title:</strong> {eventDetail.title}</p>
          <p><strong>Start:</strong> {eventDetail.start?.split('T')[0]}</p>
          <p><strong>End:</strong> {eventDetail.end?.split('T')[0]}</p>
          <p><strong>Description:</strong> {eventDetail.description || 'No description provided'}</p>
          {eventDetail.opinion && <p><strong>Opinion:</strong> {eventDetail.opinion}</p>}
          <p><strong>All Day:</strong> {eventDetail.allDay ? 'Yes' : 'No'}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setInfoModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    ) 
  }
  // Render the time-off request modal
  const renderTimeOffModal = () => (
    <Modal isOpen={modalOpen} toggle={toggleModal}>
      <ModalHeader toggle={toggleModal}>Time Off Request</ModalHeader>
      <ModalBody>
        <div>
          <FormGroup>
            <Label for="title">Title</Label>
            <Input
              type="text"
              id="title"
              placeholder="Enter title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label for="timeOffType">Time Off Type</Label>
            <Dropdown isOpen={typeDropdownOpen} toggle={toggleTypeDropdown}>
              <DropdownToggle caret color="primary" style={{ width: '100%', textAlign: 'left' }}>
                {selectedType}
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => selectType('Paid')}>Paid</DropdownItem>
                <DropdownItem onClick={() => selectType('Unpaid')}>Unpaid</DropdownItem>
                <DropdownItem onClick={() => selectType('Sick')}>Sick</DropdownItem>
                <DropdownItem onClick={() => selectType('Vacation')}>Vacation</DropdownItem>
                <DropdownItem onClick={() => selectType('MISC')}>MISC</DropdownItem>
                <DropdownItem onClick={() => selectType('ExtraHours')}>Extra Hours</DropdownItem> 
              </DropdownMenu>
            </Dropdown>
          </FormGroup>
          
          <FormGroup>
            <Label>Dates</Label>
            <Row>
              <Col>
                <Input 
                  type="text" 
                  value={dateRange.start} 
                  readOnly 
                />
              </Col>
              <Col xs="auto" className="d-flex align-items-center">
                —
              </Col>
              <Col>
                <Input 
                  type="text" 
                  value={dateRange.end} 
                  readOnly 
                />
              </Col>
            </Row>
          </FormGroup>
          
          <FormGroup>
            <Label>Is All Day? *</Label>
            <div className="d-flex align-items-center">
              <Label check className="me-3">
                <Input 
                  type="radio" 
                  name="isAllDay" 
                  checked={allday}
                  onChange={() => setAllday(true)}
                  className="me-1"
                />
                Yes
              </Label>
              <Label check>
                <Input 
                  type="radio" 
                  name="isAllDay" 
                  checked={!allday}
                  onChange={() => setAllday(false)}
                  className="me-1"
                />
                No
              </Label>
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label for="description">Description</Label>
            <Input 
              type="textarea" 
              id="description" 
              rows="3" 
              value={requestDescription}
              onChange={(e) => setRequestDescription(e.target.value)}
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Supporting Document</Label> 
            <div> 
                  <Dropzone
                    onDrop={(acceptedFiles) => {
                      handleImageListUpload(acceptedFiles);
                    }}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <div className="dropzone dz-clickable">
                        <div
                          className="dz-message needsclick"
                          {...getRootProps()}
                        >
                          <div className="mb-3">
                            <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                          </div>
                          <h5>Drop files here or click to upload.</h5>
                        </div>
                      </div>
                    )}
                  </Dropzone>
                    <div className="list-unstyled mb-0" id="file-previews">
                      {selectedFiles.map((f, i) => {
                        return (
                          <Card
                            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                            key={i + "-file"}
                          >
                            <div className="p-2">
                              <Row className="align-items-center">
                                <Col className="col-auto">
                                  <img
                                    data-dz-thumbnail=""
                                    height="80"
                                    className="avatar-sm rounded bg-light"
                                    alt={f.name}
                                    src={f.preview}
                                  />
                                </Col>
                                <Col>
                                  <Link
                                    to={f.url}
                                    target="_blank"
                                    className="text-muted font-weight-bold"
                                  >
                                    {f.name}
                                  </Link>
                                  <p className="mb-0">
                                    <strong>{f.formattedSize}</strong>
                                  </p>
                                </Col>
                                <Col>
                                  <i  onClick={() => {
                                      deleteImg(f.filename);
                                    }} className="ri-delete-bin-2-line float-end"></i>
                                </Col>
                              </Row>
                            </div>
                          </Card>
                        );
                      })}
                    </div> 
            </div>
          </FormGroup>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleSubmitRequest}>Save</Button>
        <Button color="secondary" onClick={toggleModal}>Discard</Button>
      </ModalFooter>
    </Modal>
  );
  
  // Render a single month for the year view with drag selection support
  const renderMonth = (year, monthIndex) => {
    const monthNames = [
      'January', 'February', 'March', 
      'April', 'May', 'June',
      'July', 'August', 'September', 
      'October', 'November', 'December'
    ];
    
    const daysInMonth = getDaysInMonth(year, monthIndex);
    const firstDay = getFirstDayOfMonth(year, monthIndex);
    const weeksToRender = Math.ceil((daysInMonth + firstDay) / 7);
    
    return (
      <div key={monthIndex} className="border rounded p-2" style={{ height: '100%' }}>
        <div className="text-center font-weight-bold mb-2">{monthNames[monthIndex]} {year}</div>
        <div className="d-flex justify-content-between text-center text-muted small mb-1">
          <div style={{ width: '14.28%' }}>S</div>
          <div style={{ width: '14.28%' }}>M</div>
          <div style={{ width: '14.28%' }}>T</div>
          <div style={{ width: '14.28%' }}>W</div>
          <div style={{ width: '14.28%' }}>T</div>
          <div style={{ width: '14.28%' }}>F</div>
          <div style={{ width: '14.28%' }}>S</div>
        </div>
        
        {Array(weeksToRender).fill(0).map((_, weekIndex) => (
          <div key={weekIndex} className="d-flex justify-content-between">
            {Array(7).fill(0).map((_, dayIndex) => {
              const day = weekIndex * 7 + dayIndex - firstDay + 1;
              const isValid = day > 0 && day <= daysInMonth;
              const hasEvent = isValid && hasEventOnDate(year, monthIndex, day);  
              const event = getEventOnDate(year, monthIndex, day);  
              const eventOpinion = event ? event.opinion : '';
              const timeOffType = event ? event.title : '';
              const opinionClassName = getEventClass(eventOpinion);
              const typeClassName = getTypeClass(timeOffType)
              const isToday = isValid && new Date().getFullYear() === year && 
                              new Date().getMonth() === monthIndex && 
                              new Date().getDate() === day;
              const isSelected = isValid && isDateSelected(year, monthIndex, day); 
              
              return (
                <div 
                    key={dayIndex} 
                    className={`calendar-date-cell ${isToday ? 'calendar-date-today' : ''} ${opinionClassName}  ${typeClassName}`}
                    style={{ 
                        width: '14.28%', 
                        textAlign: 'center',
                        cursor: isValid ? 'pointer' : 'default',
                        color: (isSelected || hasEvent) ? 'white' : isValid ? 'inherit' : '#dee2e6',
                        borderRadius: hasEvent || isToday || isSelected ? '20%' : '0',
                        padding: '2px 0'
                    }}
                  onClick={() => isValid && hasEvent && handleDateClick(year, monthIndex, day)}
                  onMouseDown={() => isValid && handleDateMouseDown(year, monthIndex, day, hasEvent)}
                  onMouseEnter={() => isValid && handleDateMouseEnter(year, monthIndex, day)}
                  onMouseUp={() => isValid && handleDateMouseUp(year, monthIndex, day)}
                >
                  {isValid ? day : ''}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };
  
  // Render the year view calendar with drag selection
  const renderYearView = () => {
    return (
      <div onMouseLeave={handleMouseLeave}>
        <Row>
          {Array(12).fill(0).map((_, monthIndex) => (
            <Col md={4} key={monthIndex} className="mb-4">
              {renderMonth(currentYear, monthIndex)}
            </Col>
          ))}
        </Row>
      </div>
    );
  };
  
  return (
    <Card className="mt-3">
      <CardBody>
        {renderTopNav()}
        
        <Row>
          <Col md={9}>
            {renderPendingRequests()}
            {viewMode === 'year' ? (
              renderYearView()
            ) : (
              <div className="calendar-container">
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
                  initialView="dayGridMonth"
                  themeSystem="bootstrap"
                  headerToolbar={false}
                  selectable={true}
                  select={handleDateSelect} 
                  eventClick={handleEventClick}
                  events={events}
                  height="auto"
                  contentHeight="auto"
                  initialDate={currentDate}
                />
              </div>
            )}
          </Col>
          <Col md={3}>
            {renderLegend()}
          </Col>
        </Row>
        {InfoModal()}
        {renderTimeOffModal()}
      </CardBody>
    </Card>
  );
}; 
export default TimeOffDashboard;